/**
 * GetLabsOrderModal.tsx
 *
 * GetLabs at-home blood draw, on the same order form as Axiom and Partell:
 * LabOrderModalCore with the chart's identity locked and none of the
 * lab-visit sections (presets, notes, collection dates, vitals), plus the
 * vendor's own cards in itemsSlot — Visit, Tests, Requisition, then the
 * Ordering provider last — and a summary in confirmSlot.
 *
 * The address is where the phlebotomist goes. GetLabs emails the patient a
 * link to pick a time; the order is a request until they book.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, FlaskConical, Search, ShieldCheck, Stethoscope, Syringe, Upload, X } from 'lucide-react';
import {
  createIntegrationOrder,
  getIntegrationCoverage,
  getIntegrationPrescribers,
  getIntegrationServiceability,
  type EnabledIntegration,
  type CreateIntegrationOrderResult,
  type IntegrationPrescriber,
  type LabOrderFilePayload,
} from '@/services/integrations.service';
import { updatePatient } from '@/services/patients.service';
import { searchQuestTests, type QuestTestCode } from '@/services/labs.service';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui';
import { LabOrderModalCore, type LabOrderConfig, type LabOrderSubmitResult } from './LabOrderModalCore';
import { OrderSection } from './orderFormParts';
import { useAuthStore } from '@/store/authStore';

interface GetLabsOrderModalProps {
  patientId: number;
  integration: EnabledIntegration;
  isOpen: boolean;
  onClose: () => void;
  onOrdered: (result: CreateIntegrationOrderResult) => void;
}

const field = 'text-xs font-medium text-slate-600';

/** What is still missing before the visit can be requested. Pure, for tests. */
export function getLabsProblems(o: {
  zipBlocked: boolean;
  testCount: number;
  hasFile: boolean;
  contactName: string;
  contactPhone: string;
}): string[] {
  const problems: string[] = [];
  if (o.zipBlocked) problems.push("an address GetLabs services (this ZIP is outside coverage)");
  if (o.testCount === 0 && !o.hasFile && !o.contactName.trim() && !o.contactPhone.trim()) {
    problems.push('at least one test, a requisition, or the ordering provider');
  }
  return problems;
}

export function GetLabsOrderModal({ patientId, integration, isOpen, onClose, onOrdered }: GetLabsOrderModalProps) {
  const accountId = useAuthStore((s) => s.accountId);
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  // Which account physician the provider fields came from ('' = typed by hand).
  const [prescriberUserId, setPrescriberUserId] = useState<number | ''>('');
  const [file, setFile] = useState<LabOrderFilePayload | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [tests, setTests] = useState<QuestTestCode[]>([]);
  const [testQuery, setTestQuery] = useState('');
  const [testResults, setTestResults] = useState<QuestTestCode[]>([]);
  const [testSearching, setTestSearching] = useState(false);

  useEffect(() => {
    if (isOpen) return;
    setContactName(''); setContactPhone(''); setPrescriberUserId('');
    setFile(null); setFileError(null);
    setTests([]); setTestQuery(''); setTestResults([]);
  }, [isOpen]);

  // Same physician list as the Axiom and Partell prescriber pickers.
  const { data: prescribers = [] } = useQuery({
    queryKey: ['integration-prescribers', accountId],
    queryFn: getIntegrationPrescribers,
    enabled: isOpen,
    staleTime: 5 * 60_000,
  });
  const { data: coverage } = useQuery({
    queryKey: ['integration-coverage', integration.key, patientId],
    queryFn: () => getIntegrationCoverage(patientId, integration.key),
    enabled: isOpen,
    staleTime: 30_000,
  });
  const { data: service } = useQuery({
    queryKey: ['integration-serviceability', integration.key, patientId],
    queryFn: () => getIntegrationServiceability(patientId, integration.key),
    enabled: isOpen,
    staleTime: 60_000,
  });
  const zipBlocked = service !== undefined && !service.serviceable && !service.unknown;

  const applyPrescriber = (id: number) => {
    const p = prescribers.find((x: IntegrationPrescriber) => x.userId === id);
    if (!p) { setPrescriberUserId(''); return; }
    setPrescriberUserId(p.userId);
    setContactName(`${p.firstName} ${p.lastName}`.trim() + (p.credential ? `, ${p.credential}` : ''));
    if (p.phone) setContactPhone(p.phone);
  };

  // Quest test search, debounced.
  useEffect(() => {
    const q = testQuery.trim();
    if (q.length < 2) { setTestResults([]); return; }
    let cancelled = false;
    setTestSearching(true);
    const t = setTimeout(() => {
      searchQuestTests(q)
        .then((r) => { if (!cancelled) setTestResults(r ?? []); })
        .catch(() => { if (!cancelled) setTestResults([]); })
        .finally(() => { if (!cancelled) setTestSearching(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [testQuery]);

  const addTest = (t: QuestTestCode) => {
    setTests((cur) => (cur.some((x) => x.testCode === t.testCode) ? cur : [...cur, t]));
    setTestQuery('');
    setTestResults([]);
  };

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    setFileError(null);
    if (!f) { setFile(null); return; }
    if (f.size > 5 * 1024 * 1024) { setFileError('File is larger than 5 MB.'); return; }
    const dataUrl = await new Promise<string>((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(String(r.result || ''));
      r.onerror = () => rej(r.error);
      r.readAsDataURL(f);
    });
    setFile({ name: f.name, dataBase64: dataUrl.replace(/^data:[^;]+;base64,/, '') });
  }

  const problems = getLabsProblems({ zipBlocked, testCount: tests.length, hasFile: !!file, contactName, contactPhone });

  // Stable refs so config.submit / validateExtra never read stale state.
  const stateRef = useRef({ contactName, contactPhone, file, tests, problems });
  stateRef.current = { contactName, contactPhone, file, tests, problems };
  const onOrderedRef = useRef(onOrdered);
  onOrderedRef.current = onOrdered;

  const config = useMemo<LabOrderConfig>(() => ({
    vendor: 'getlabs',
    title: 'Schedule GetLabs Visit',
    successTitle: 'Visit Requested',
    requireAddress: true,
    // Same as Axiom and Partell: identity is the chart's, no lab-visit UI.
    lockPatientIdentity: true,
    features: {
      presets: false, facilityProvider: false, billing: false, diagnoses: false, tests: false,
      notes: false, collectionDate: false, vitals: false,
    },
    addressWording: {
      label: 'Draw location',
      notice: 'Where the phlebotomist goes, for this visit only.',
      editHint: '…or edit the fields below for a different draw address.',
    },
    validateExtra: () => {
      const p = stateRef.current.problems;
      return p.length > 0 ? `Still needed: ${p.join(', ')}` : null;
    },
    submit: async (payload): Promise<LabOrderSubmitResult> => {
      const { contactName: cn, contactPhone: cp, file: f, tests: picked } = stateRef.current;
      // A lab order may FILL a missing demographic, never change one.
      if (payload.demographicsFill) {
        await updatePatient(payload.patientId, payload.demographicsFill);
      }
      const result = await createIntegrationOrder(payload.patientId, {
        key: 'getlabs',
        metadata: {
          shippingAddress: {
            recipientName: payload.recipientName || undefined,
            line1: payload.street,
            line2: payload.street2 || undefined,
            city: payload.city,
            state: payload.state,
            zip: payload.zip,
            phone: payload.phone || undefined,
            patientShippingAddressId: payload.shippingAddressId ?? undefined,
          },
        },
        labOrderDetails: {
          contactName: cn.trim() || undefined,
          contactPhone: cp.trim() || undefined,
          testCodes: picked.length > 0 ? picked.map((t) => t.testCode) : undefined,
          file: f ?? undefined,
        },
      });
      onOrderedRef.current(result);
      return { ok: true, lines: [`GetLabs visit requested — Order #${result.integrationOrderId}. GetLabs will email the patient a link to pick a time.`] };
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  const product = integration.products[0];
  const price = product
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: product.currency || 'USD' }).format(product.price)
    : '';

  const itemsSlot = (
    <div className="space-y-4">
      <OrderSection
        icon={<Syringe className="h-3.5 w-3.5 text-slate-400" />}
        title="Visit"
        hint="An at-home blood draw. GetLabs emails the patient a link to pick a time; the visit is booked when they do."
      >
        <div className="space-y-2">
          {product && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-800">{product.name}</span>
              <span className="text-sm font-semibold text-slate-900">{price}</span>
            </div>
          )}
          {coverage?.covered && coverage.membership && (
            <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
              <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
              <p className="text-xs text-emerald-800">
                Patient has <span className="font-semibold">{coverage.membership.title}</span> — this visit is covered by the account.
              </p>
            </div>
          )}
          {zipBlocked && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              <span className="font-semibold">GetLabs doesn&apos;t service this area.</span>{' '}
              The ZIP{service?.zip ? ` ${service.zip}` : ''} is outside coverage — change the draw location or use another lab.
            </p>
          )}
          {service?.unknown && service.reason && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] text-amber-800">
              Coverage not confirmed: {service.reason}
            </p>
          )}
        </div>
      </OrderSection>

      <OrderSection
        icon={<FlaskConical className="h-3.5 w-3.5 text-slate-400" />}
        title="Tests"
        hint="Optional. Quest test codes for the draw. A requisition PDF can carry them instead."
      >
        <div className="relative">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-slate-400" />
            <Input
              value={testQuery}
              onChange={(e) => setTestQuery(e.target.value)}
              placeholder="Search by test code or name…"
              aria-label="Search tests"
            />
            {testSearching && <Spinner className="h-4 w-4" />}
          </div>
          {testResults.length > 0 && (
            <div className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
              {testResults.map((t) => (
                <button key={t.testCode} type="button" onClick={() => addTest(t)}
                  className="w-full border-b border-slate-100 px-3 py-2 text-left text-sm last:border-0 hover:bg-blue-50">
                  <span className="mr-2 font-mono text-xs text-slate-500">{t.testCode}</span>
                  <span className="text-slate-800">{t.description}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {tests.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {tests.map((t) => (
              <li key={t.testCode} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-1.5">
                <span className="text-sm text-slate-800">
                  <span className="mr-2 font-mono text-xs text-slate-500">{t.testCode}</span>{t.description}
                </span>
                <button type="button" aria-label={`Remove ${t.description}`}
                  onClick={() => setTests((cur) => cur.filter((x) => x.testCode !== t.testCode))}
                  className="text-slate-400 hover:text-slate-700">
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </OrderSection>

      <OrderSection
        icon={<FileText className="h-3.5 w-3.5 text-slate-400" />}
        title="Requisition"
        hint="Optional. A requisition PDF (max 5 MB) for GetLabs to bring to the draw."
      >
        {file ? (
          <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-1.5 text-sm">
            <span className="truncate text-slate-700">{file.name}</span>
            <button type="button" onClick={() => setFile(null)} className="text-slate-400 hover:text-slate-700" aria-label="Remove file">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-slate-300 px-3 py-2 text-xs text-slate-500 hover:border-slate-400">
            <Upload className="h-3.5 w-3.5" />
            Upload PDF
            <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => void handleFile(e)} />
          </label>
        )}
        {fileError && <p className="mt-1 text-[11px] text-red-600">{fileError}</p>}
      </OrderSection>

      <OrderSection
        icon={<Stethoscope className="h-3.5 w-3.5 text-slate-400" />}
        title="Ordering provider"
        hint="Who GetLabs contacts about the draw. Pick a practice physician to fill it in, or type it."
      >
        <div className="space-y-2">
          {prescribers.length > 0 && (
            <label className="block space-y-1">
              <span className={field}>Practice physician</span>
              <select
                value={prescriberUserId}
                onChange={(e) => applyPrescriber(Number(e.target.value))}
                className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
              >
                <option value="">Enter manually…</option>
                {prescribers.map((p: IntegrationPrescriber) => (
                  <option key={p.userId} value={p.userId}>
                    {p.firstName} {p.lastName}{p.credential ? `, ${p.credential}` : ''}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <label className="space-y-1"><span className={field}>Name</span>
              <Input value={contactName} onChange={(e) => { setContactName(e.target.value); setPrescriberUserId(''); }} placeholder="Dr. Last Name" /></label>
            <label className="space-y-1"><span className={field}>Phone</span>
              <Input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="(555) 555-5555" /></label>
          </div>
        </div>
      </OrderSection>
    </div>
  );

  const confirmSlot = (
    <div className="space-y-1">
      <p className="font-semibold text-blue-900">GetLabs visit{product ? ` — ${price}` : ''}</p>
      {tests.length > 0 && (
        <p className="text-blue-800">Tests: {tests.map((t) => `${t.testCode} ${t.description}`).join('; ')}</p>
      )}
      {file && <p className="text-blue-800">Requisition: {file.name}</p>}
      {(contactName.trim() || contactPhone.trim()) && (
        <p className="text-blue-800">Ordering provider: {[contactName.trim(), contactPhone.trim()].filter(Boolean).join(' · ')}</p>
      )}
      <p className="pt-1 text-blue-800">GetLabs will email the patient a link to pick a time.</p>
    </div>
  );

  return (
    <LabOrderModalCore
      config={config}
      patientId={patientId}
      open={isOpen}
      onClose={onClose}
      onOrdered={() => { /* reported from config.submit */ }}
      itemsSlot={itemsSlot}
      confirmSlot={confirmSlot}
    />
  );
}
